// frontend/src/components/pages/chat/ModelDropdown.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronDown,
  Search,
  Loader2,
  Check,
  Image as ImageIcon,
  FileText,
  Search as SearchIcon,
  UploadCloud,
  DownloadCloud,
  Filter as FilterIcon,
  Lock,
  ArrowRight,
  Loader,
} from "lucide-react";

// Import provider logos
import ChatGPTLogo from "../../../media/svg/models/chatgpt.svg";
import ClaudeLogo from "../../../media/svg/models/claude.svg";
import DeepSeekLogo from "../../../media/svg/models/deepseek.svg";
import FluxLogo from "../../../media/svg/models/flux.svg";
import GeminiLogo from "../../../media/svg/models/gemini.svg";
import LlamaLogo from "../../../media/svg/models/llama.svg";
import MistralLogo from "../../../media/svg/models/mistral.svg";
import PerplexityLogo from "../../../media/svg/models/perplexity.svg";
import QwenLogo from "../../../media/svg/models/qwen.svg";
import StabilityLogo from "../../../media/svg/models/stability.svg";
import xAILogo from "../../../media/svg/models/xai.svg";

// Function to get provider logo
const getProviderLogo = (providerKey) => {
  const logoMap = {
    openai: ChatGPTLogo,
    chatgpt: ChatGPTLogo,
    anthropic: ClaudeLogo,
    claude: ClaudeLogo,
    deepseek: DeepSeekLogo,
    flux: FluxLogo,
    google: GeminiLogo,
    gemini: GeminiLogo,
    meta: LlamaLogo,
    llama: LlamaLogo,
    mistral: MistralLogo,
    perplexity: PerplexityLogo,
    qwen: QwenLogo,
    stability: StabilityLogo,
    xai: xAILogo,
    "stability ai": StabilityLogo,
  };

  return logoMap[providerKey.toLowerCase()] || null;
};

const ModelDropdown = ({
  models = [],
  selectedModel = "",
  onSelect = () => { },
  isLoading = false,
  placeholder = "Select a model",
  forceOpen = false,
  isUsingFallbackModel = false, // Track if fallback model is active
}) => {
  const [isOpen, setIsOpen] = useState(forceOpen);
  const [search, setSearch] = useState("");
  const [recentModels, setRecentModels] = useState(() => {
    // Load recent models from localStorage
    try {
      const saved = localStorage.getItem("recentModels");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const FILTERS = [
    {
      key: "all",
      label: "All",
      icon: <FilterIcon size={16} strokeWidth={1.5} />,
      filter: () => true,
    },
    {
      key: "search",
      label: "Search",
      icon: <SearchIcon size={16} strokeWidth={1.5} />,
      filter: (model) => model.support?.search,
    },
    {
      key: "imageIn",
      label: "Input Images",
      icon: <UploadCloud size={16} strokeWidth={1.5} />,
      filter: (model) => model.support?.imageIn,
    },
    {
      key: "imageOut",
      label: "Generate Images",
      icon: <ImageIcon size={16} strokeWidth={1.5} />,
      filter: (model) => model.support?.imageOut,
    },
    {
      key: "pdf",
      label: "Read PDF",
      icon: <FileText size={16} strokeWidth={1.5} />,
      filter: (model) => model.support?.pdf,
    },
  ];

  const MODEL_TYPE_FILTERS = [
    {
      key: "all",
      label: "All",
      filter: () => true,
    },
    {
      key: "text",
      label: "Text",
      filter: (model) => model.modelType === "text",
    },
    {
      key: "image",
      label: "Image",
      filter: (model) => model.modelType === "image",
    },
    // {
    //   key: 'animation',
    //   label: 'Animation',
    //   filter: (model) => model.modelType === 'animation',
    //   comingSoon: true,
    // },
  ];

  const [activeFilter, setActiveFilter] = useState("all");
  const [activeModelTypeFilter, setActiveModelTypeFilter] = useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);
  const filterButtonRef = useRef(null);

  // Group models by provider (use only provider field from API)
  const groupModelsByProvider = (modelsList) => {
    const groups = {};
    const providerDisplayNames = {};
    modelsList.forEach((model) => {
      let providerRaw = model.provider;
      if (
        !providerRaw ||
        typeof providerRaw !== "string" ||
        providerRaw.trim() === ""
      ) {
        providerRaw = "Other";
      }
      const providerKey = providerRaw.trim().toLowerCase();
      if (!groups[providerKey]) {
        groups[providerKey] = [];
        providerDisplayNames[providerKey] = providerRaw;
      }
      const modelName = model.name || model.label || "Unknown Model";
      groups[providerKey].push({ ...model, _dropdownName: modelName });
    });
    return { groups, providerDisplayNames };
  };

  // Map and filter models for display
  // Only include active models in the dropdown
  const mappedModels = models
    .filter((m) => m.active !== false)
    .map((model) => ({
      ...model,
      label: model.title || model.name || "Auto",
      value: model._id,
      originalName: model.name,
    }));

  // Filter models by search, active filter, and model type filter
  // Also exclude Auto models from provider groups since we show them separately
  const filteredModels = mappedModels.filter(
    (model) =>
      model.name !== "Auto" && // Exclude Auto models from provider groups
      model.label !== "Auto" &&
      ((model.name || model.label || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
        (model.provider || "").toLowerCase().includes(search.toLowerCase())) &&
      (FILTERS.find((f) => f.key === activeFilter)?.filter(model) ?? true) &&
      (MODEL_TYPE_FILTERS.find((f) => f.key === activeModelTypeFilter)?.filter(
        model
      ) ??
        true)
  );
  const { groups, providerDisplayNames } =
    groupModelsByProvider(filteredModels);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Focus search input when opening (with slight delay to ensure dropdown is rendered)
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Use requestAnimationFrame for immediate focus without delay
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // If forceOpen changes, update isOpen accordingly
  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen]);

  // Close filter dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
    }
    if (showFilterDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterDropdown]);

  const handleSelect = (model) => {
    const modelId = model._id?.$oid || model.value || model.name;
    onSelect(modelId);

    // Update recent models
    setRecentModels((prev) => {
      const filtered = prev.filter((id) => id !== modelId);
      const updated = [modelId, ...filtered].slice(0, 3); // Keep only 3 recent models
      localStorage.setItem("recentModels", JSON.stringify(updated));
      return updated;
    });

    setIsOpen(false);
    setSearch("");
  };

  // Handle Auto model selection or find the selected model
  const autoModel =
    models.find((m) => m.isAuto) ||
    mappedModels.find((m) => m.isAuto) ||
    mappedModels.find((m) => (m.name || "").toLowerCase().includes("auto")) ||
    mappedModels.find((m) => m.isDefault) ||
    mappedModels.find((m) => (m.name || "").toLowerCase() === "openrouter/auto");

  const isAutoSelected =
    selectedModel === "" ||
    selectedModel === "auto" ||
    (!!autoModel &&
      (selectedModel === autoModel._id ||
        selectedModel === autoModel.value ||
        selectedModel === autoModel.name ||
        selectedModel === autoModel?._id?.$oid));

  // If using fallback model, always show "Default" in header
  const selected = isUsingFallbackModel
    ? {
        label: "Default",
        name: "Default",
        value: selectedModel || "", // Keep original model ID for reference
      }
    : isAutoSelected
    ? {
        label: "Default",
        name: "Default",
        value: "",
      }
    : filteredModels.find(
        (m) =>
          m._id?.$oid === selectedModel ||
          m.value === selectedModel ||
          m.name === selectedModel
      ) ||
      mappedModels.find(
        (m) =>
          m._id?.$oid === selectedModel ||
          m.value === selectedModel ||
          m.name === selectedModel
      ) ||
      mappedModels[0];

  return (
    <div className="model-dropdown relative w-full" ref={dropdownRef}>
      {!forceOpen && (
        <button
          type="button"
          className="model-dropdown-toggle w-full flex items-center justify-between px-4 py-2 text-sm font-medium bg-ac-02 text-white dark:text-black/70 hover:text-fg-80 hover:bg-ac-01 rounded-full transition-all duration-200 group gqp-3"
          onClick={() => setIsOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={isLoading}
          title="Select Model"
        >
          <span className="flex items-center gap-2 truncate">
            {/* Only show icon if not Default/Auto */}
            {selected && selected.label !== "Default" && selected.icon && (
              <span className="inline-flex items-center justify-center w-3">
                {selected.icon}
              </span>
            )}
            <span>{selected?.label || selected?.name || placeholder}</span>
          </span>
          <ChevronDown
            size={12}
            className={`text-fg-60 opacity-70 text-white dark:text-black/70 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
              }`}
          />
        </button>
      )}
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      {isOpen && (
        <div
          className="model-dropdown-list fixed md:absolute top-auto md:top-full bottom-0 md:bottom-auto left-0 right-0 md:left-0 md:right-auto md:mt-2 bg-white dark:bg-bg-60 rounded-t-2xl md:rounded-2xl shadow-xl border border-bd-50 z-50 overflow-hidden animate-slide-up md:animate-none md:w-96 md:max-w-md md:min-w-[320px]"
          style={{ 
            maxHeight: "85vh"
          }}
        >
          {/* Mobile header with close button */}
          <div className="flex items-center justify-between p-4 pb-2 md:hidden border-b border-bd-50">
            <h3 className="text-sm font-medium text-fg-50">Select Model</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-fg-60 hover:text-fg-50 hover:bg-bg-50 transition-colors touch-manipulation"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          {/* Search Bar */}
          <div className="p-4 pb-2 md:pt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-bg-50 relative">
              <Search size={16} className="text-fg-60" />
              <input
                ref={inputRef}
                type="text"
                className="model-dropdown-search bg-transparent outline-none w-full text-fg-50 placeholder-fg-60 text-sm"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search models"
              />
              <button
                type="button"
                ref={filterButtonRef}
                className={`p-1 rounded transition-colors ${showFilterDropdown ? "bg-bg-40" : "hover:bg-bg-40"
                  }`}
                title="Filter"
                onClick={() => setShowFilterDropdown((prev) => !prev)}
              >
                <FilterIcon size={16} className="text-fg-60" />
              </button>
            </div>

            {/* Filter Dropdown */}
            {showFilterDropdown && (
              <div
                ref={filterDropdownRef}
                className="absolute right-4 top-16 z-50 bg-bg-60 rounded-xl shadow-lg border border-bd-50 min-w-[200px]"
              >
                <div className="px-4 py-2 text-xs font-normal text-fg-60 bg-bg-60">
                  Filter by
                </div>
                {FILTERS.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={`w-full px-4 py-2 text-left text-xs font-normal flex items-center gap-2 ${activeFilter === filter.key
                        ? "bg-ac-02/10 text-ac-02"
                        : "text-fg-50 hover:bg-bg-50"
                      } transition-colors duration-150`}
                    onClick={() => {
                      setActiveFilter(filter.key);
                      setShowFilterDropdown(false);
                    }}
                  >
                    {/* Icon with matching colors */}
                    {filter.key === "search" && (
                      <SearchIcon
                        size={16}
                        strokeWidth={1.5}
                        className="text-green-600 dark:text-green-400"
                      />
                    )}
                    {filter.key === "imageIn" && (
                      <UploadCloud
                        size={16}
                        strokeWidth={1.5}
                        className="text-blue-600 dark:text-blue-400"
                      />
                    )}
                    {filter.key === "imageOut" && (
                      <ImageIcon
                        size={16}
                        strokeWidth={1.5}
                        className="text-purple-600 dark:text-purple-400"
                      />
                    )}
                    {filter.key === "pdf" && (
                      <FileText
                        size={16}
                        strokeWidth={1.5}
                        className="text-yellow-600 dark:text-yellow-400"
                      />
                    )}
                    {filter.key === "all" && (
                      <FilterIcon
                        size={16}
                        strokeWidth={1.5}
                        className="text-fg-60"
                      />
                    )}
                    <span>{filter.label}</span>
                    {activeFilter === filter.key && (
                      <Check size={14} className="ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Model Type Filter Tabs */}
          <div className="px-4 pb-2">
            <div className="flex items-center gap-1">
              {MODEL_TYPE_FILTERS.map((filter) => {
                // Calculate count based on all mapped models, not filtered models
                const count = mappedModels.filter((m) =>
                  filter.filter(m)
                ).length;

                return (
                  <button
                    key={filter.key}
                    type="button"
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeModelTypeFilter === filter.key
                        ? "bg-ac-02 text-white dark:text-black/70"
                        : filter.key === "animation"
                          ? "text-fg-70 cursor-not-allowed opacity-60"
                          : "text-fg-60 hover:text-fg-50 hover:bg-bg-50"
                      }`}
                    onClick={() =>
                      !filter.comingSoon && setActiveModelTypeFilter(filter.key)
                    }
                    title={filter.comingSoon ? "Coming Soon" : undefined}
                    disabled={filter.comingSoon}
                  >
                    {filter.label}
                    <span className="ml-1 text-xs opacity-60">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[60vh] md:max-h-[500px] overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-fg-60 mb-2" />
                <span className="text-sm text-fg-60">Loading models...</span>
              </div>
            ) : models.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <span className="text-sm text-fg-60 mb-1">
                  No models available
                </span>
                <span className="text-xs text-fg-70">
                  Please check your connection
                </span>
              </div>
            ) : filteredModels.length === 0 ? (
              <div className="text-center text-fg-60 py-6 text-sm">
                No models found
              </div>
            ) : (
              <div className="space-y-4 p-4 pt-0">
                {/* Auto Model Section - Always show at top when no search */}
                {search === "" && (
                  <div className="bg-bg-50 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-fg-50 mb-2">
                      Smart Selection
                    </h4>
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors group ${selectedModel === "" || selectedModel === "auto"
                          ? "bg-bg-40"
                          : "hover:bg-bg-40"
                        }`}
                      onClick={() => {
                        onSelect("");
                        setIsOpen(false);
                        setSearch("");
                      }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-fg-50">
                            Auto
                          </span>
                          {/* <span className="text-[9px] bg-ac-02/10 text-ac-02 px-1.5 py-0.5 rounded-full font-medium">
                            RECOMMENDED
                          </span> */}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] text-fg-60">
                            Automatically selects the best model for your task
                          </span>
                        </div>
                      </div>
                      <ArrowRight
                        size={16}
                        strokeWidth={1.5}
                        className="text-fg-60 opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </button>
                  </div>
                )}

                {/* Recent Section - Only show in "All" tab and when no search */}
                {search === "" &&
                  activeModelTypeFilter === "all" &&
                  recentModels.length > 0 && (
                    <div className="bg-bg-50 rounded-lg p-3 space-y-2">
                      <h4 className="text-sm font-medium text-fg-50 mb-2">
                        Recent
                      </h4>
                      {recentModels
                        .map((modelId, idx) => {
                          // Find the model in mappedModels
                          const model = mappedModels.find(
                            (m) =>
                              (m._id?.$oid || m.value || m.name) === modelId
                          );

                          if (!model) return null; // Skip if model not found

                          const isSelected = selectedModel === modelId;
                          return (
                            <button
                              key={`recent-${modelId}-${idx}`}
                              type="button"
                              className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors group ${isSelected ? "bg-bg-40" : "hover:bg-bg-40"
                                }`}
                              onClick={() => handleSelect(model)}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-fg-50">
                                    {model.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] text-fg-60">
                                    Includes
                                  </span>
                                  {model.support?.imageIn && (
                                    <span className="inline-flex items-center gap-1 text-[9px] text-blue-600 dark:text-blue-400 font-light">
                                      <UploadCloud size={10} />
                                      Image input
                                    </span>
                                  )}
                                  {model.support?.pdf && (
                                    <span className="inline-flex items-center gap-1 text-[9px] text-yellow-600 dark:text-yellow-400 font-light">
                                      <FileText size={10} />
                                      PDF
                                    </span>
                                  )}
                                  {model.support?.search && (
                                    <span className="inline-flex items-center gap-1 text-[9px] text-green-600 dark:text-green-400 font-light">
                                      <SearchIcon size={10} />
                                      Web Search
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ArrowRight
                                size={16}
                                strokeWidth={1.5}
                                className="text-fg-60 opacity-0 group-hover:opacity-100 transition-opacity"
                              />
                            </button>
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  )}

                {/* Provider Groups */}
                {Object.entries(groups).map(([providerKey, providerModels]) => (
                  <div
                    key={providerKey}
                    className="bg-bg-50 rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {/* Provider Icon */}
                      {(() => {
                        const logo = getProviderLogo(providerKey);
                        if (logo) {
                          return (
                            <div className="w-5 h-5 flex items-center justify-center">
                              <img
                                src={logo}
                                alt={providerDisplayNames[providerKey]}
                                className="w-5 h-5 object-contain"
                              />
                            </div>
                          );
                        } else {
                          // Fallback to letter-based icons
                          return (
                            <div className="w-5 h-5 rounded-full bg-bg-40 flex items-center justify-center">
                              <span className="text-xs font-bold text-fg-50">
                                {providerDisplayNames[providerKey]
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            </div>
                          );
                        }
                      })()}
                      <h4 className="text-sm font-medium text-fg-50">
                        {providerDisplayNames[providerKey]}
                      </h4>
                    </div>

                    {providerModels.map((model, idx) => {
                      const isSelected =
                        selectedModel ===
                        (model._id?.$oid || model.value || model.name);
                      return (
                        <button
                          key={`${providerKey}-${model._id || model.value || model.name
                            }-${idx}`}
                          type="button"
                          className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors group ${isSelected ? "bg-bg-40" : "hover:bg-bg-40"
                            }`}
                          onClick={() => handleSelect(model)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-fg-50">
                                {model.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] text-fg-60 font-normal">
                                Includes
                              </span>
                              {model.support?.imageIn && (
                                <span className="inline-flex items-center gap-1 text-[9px] text-blue-600 dark:text-blue-400 font-light">
                                  <UploadCloud size={10} />
                                  Image input
                                </span>
                              )}
                              {model.support?.imageOut && (
                                <span className="inline-flex items-center gap-1 text-[9px] text-purple-600 dark:text-purple-400 font-light">
                                  <ImageIcon size={10} />
                                  Image output
                                </span>
                              )}
                              {model.support?.pdf && (
                                <span className="inline-flex items-center gap-1 text-[9px] text-yellow-600 dark:text-yellow-400 font-light">
                                  <FileText size={10} />
                                  PDF
                                </span>
                              )}
                              {model.support?.search && (
                                <span className="inline-flex items-center gap-1 text-[9px] text-green-600 dark:text-green-400 font-light">
                                  <SearchIcon size={10} />
                                  Web Search
                                </span>
                              )}
                            </div>
                          </div>
                          <ArrowRight
                            size={16}
                            strokeWidth={1.5}
                            className="text-fg-60 opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelDropdown;
